import { useState } from 'react';
import { Check, Star, RocketLaunch, Crown } from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';
import { useScreenView } from '../../../utils/ScreenViewContext';
import useFrontChat from '../../../hooks/useFrontChat';
import TrustSignals from '../TrustSignals';
import SignupFooter from '../SignupFooter';
import { setApplicationStep } from '../../../pages/signup';
import { Application } from '../../../types/dbTypes';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
  TierType,
} from '../../../types/apiTypes';
import { asTierType } from '../../../utils/tierSelection';
import {
  TIER_CONFIG,
  TIER_BASE_FEATURES,
  TIER_PLUS_FEATURES,
} from './TierReviewCard';
import { CARRIER_NUMBER_ERROR_STORAGE_KEY } from '../../../utils/carrierIdentity';
import { safeSessionSetItem } from '../../../shared/safeStorage';

interface ChoosePlanProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application: Application;
  setApplication: (application: Application) => void;
}

// Extended tier info for the step body, moved here from the deleted TierSelectionModal.
const TIER_DETAILS: Record<
  TierType,
  {
    grayItems?: string[];
    extras: { label: string; value: string }[];
  }
> = {
  [TierType.STARTER]: {
    extras: [
      { label: '24/7 Roadside', value: '$149/use' },
      { label: '$0-Deposit Rentals', value: '$199/use' },
    ],
  },
  [TierType.CORE]: {
    extras: [
      { label: '24/7 Roadside', value: '$99/use' },
      { label: '$0-Deposit Rentals', value: '$129/use' },
    ],
  },
  [TierType.PRO]: {
    extras: [
      { label: '24/7 Roadside', value: 'Included' },
      { label: '$0-Deposit Rentals', value: 'Included' },
    ],
  },
};

// Centered "PLUS" rule separating the shared base rows from a tier's extras (spec-tht773-775).
function PlusDivider() {
  return (
    <li
      className="my-3 flex items-center gap-2"
      data-testid="tier-plus-divider"
    >
      <span className="h-px flex-1 border-t border-gray-200" />
      <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">
        PLUS
      </span>
      <span className="h-px flex-1 border-t border-gray-200" />
    </li>
  );
}

const TIER_ICONS = {
  [TierType.STARTER]: RocketLaunch,
  [TierType.CORE]: Star,
  [TierType.PRO]: Crown,
};

const TIERS: TierType[] = [TierType.STARTER, TierType.CORE, TierType.PRO];

function ChoosePlan({
  currStep,
  selectedRole,
  setStep,
  application,
  setApplication,
}: ChoosePlanProps) {
  useFrontChat();

  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const { userId } = useUser();
  // Seeded from whatever the application already carries (Review's Edit/Back both remount this
  // component) — otherwise a tier already chosen and saved renders as if nothing were picked.
  const [selectedTier, setSelectedTier] = useState<TierType | null>(() =>
    asTierType(application?.selected_tier)
  );
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleSetLoading = (value: boolean) => {
    setLoading(value);
  };

  const handleContinue = async () => {
    if (!selectedTier) return;

    const nextStep = await setApplicationStep({
      step: ApplicationStep.Review,
      application_id: application?.application_id,
      customer_id: userId ?? '',
      callProtectedApi,
      setLoading: handleSetLoading,
      previousStep: ApplicationStep.ChoosePlan,
      applicationStatus: application?.status as ApplicationStatus,
      additionalFields: { selected_tier: selectedTier },
      // Review reads the tier back off the application, and the page only fetches that record
      // once (when the user record lands). Without this the next step would render whatever
      // tier the record already carried — the landing-page cookie's pick, not this one.
      onApplicationUpdated: setApplication,
      // Moved from AccountInfo's submit (THT-752 behaviour, unchanged): the Highway lookup now
      // fires on this transition instead, so a bad MC/DOT has to bounce back from here.
      onInvalidIdentifier: () => {
        toast.error(
          'Invalid authority number provided. Please see the warning message.'
        );
        safeSessionSetItem(CARRIER_NUMBER_ERROR_STORAGE_KEY, 'true');
        setStep(ApplicationStep.BusinessInfo);
      },
    });
    // Guard against overriding the invalid-identifier bounce above: only advance when the
    // backend actually returned a step (undefined on a failed / invalid-carrier transition).
    if (nextStep) {
      setStep(nextStep);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {!loading ? (
              <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
                <div className="flex w-full justify-center px-6 lg:px-12">
                  <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
                    <div className="mx-auto flex-1 lg:w-[1050px] lg:max-w-[1050px]">
                      <div className="mx-auto mb-6 max-w-[1000px] text-center lg:mb-8 lg:max-w-full">
                        <h2 className="text-[30px] font-bold text-primary">
                          Choose Your Membership Tier
                        </h2>
                        <p className="text-gray-500">
                          Select the plan that best fits your business needs.
                        </p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-3 md:gap-6">
                        {TIERS.map((tier) => {
                          const config = TIER_CONFIG[tier];
                          const details = TIER_DETAILS[tier];
                          const TierIcon = TIER_ICONS[tier];
                          const isSelected = tier === selectedTier;

                          return (
                            <div
                              key={tier}
                              role="button"
                              tabIndex={0}
                              data-testid={`tier-card-${tier}`}
                              aria-label={`${config.name}, ${config.price}`}
                              aria-pressed={isSelected}
                              onClick={() => setSelectedTier(tier)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedTier(tier);
                                }
                              }}
                              className={`relative flex cursor-pointer flex-col rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
                                isSelected
                                  ? 'border-accent bg-bgyellow'
                                  : 'border-bordergray bg-white hover:border-accent'
                              }`}
                            >
                              <div className="mb-5 mt-2 text-center">
                                <div className="mb-2 flex items-center justify-center gap-2">
                                  <TierIcon
                                    size={24}
                                    weight="fill"
                                    className="text-accent"
                                  />
                                  <h3 className="text-2xl font-bold text-primary">
                                    {config.name}
                                  </h3>
                                </div>
                                <p className="text-xl font-semibold text-accent">
                                  {config.price}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  {config.tagline}
                                </p>
                              </div>

                              <ul className="mb-5 flex-1 space-y-2.5">
                                {TIER_BASE_FEATURES.map((feature, idx) => (
                                  <li
                                    key={`base-${idx}`}
                                    className="flex items-start gap-2 text-sm"
                                  >
                                    <Check
                                      size={18}
                                      weight="bold"
                                      className="mt-0.5 flex-shrink-0 text-green-600"
                                    />
                                    <span className="text-gray-700">
                                      {feature}
                                    </span>
                                  </li>
                                ))}
                                {TIER_PLUS_FEATURES[tier].length > 0 && (
                                  <PlusDivider />
                                )}
                                {TIER_PLUS_FEATURES[tier].map(
                                  (feature, idx) => (
                                    <li
                                      key={`plus-${idx}`}
                                      className="flex items-start gap-2 text-sm"
                                    >
                                      <Check
                                        size={18}
                                        weight="bold"
                                        className="mt-0.5 flex-shrink-0 text-green-600"
                                      />
                                      <span className="text-gray-700">
                                        {feature}
                                      </span>
                                    </li>
                                  )
                                )}
                                {details.grayItems?.map((item, idx) => (
                                  <li
                                    key={`gray-${idx}`}
                                    className="flex items-start gap-2 text-sm"
                                  >
                                    <Check
                                      size={18}
                                      weight="bold"
                                      className="mt-0.5 flex-shrink-0 text-gray-400"
                                    />
                                    <span className="text-gray-500">
                                      {item}
                                    </span>
                                  </li>
                                ))}
                              </ul>

                              <div className="space-y-2 border-t border-gray-200 pt-4">
                                {details.extras.map((extra, idx) => (
                                  <div
                                    key={idx}
                                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                                      extra.value === 'Included'
                                        ? 'bg-green-50'
                                        : 'bg-gray-50'
                                    }`}
                                  >
                                    <span className="text-gray-600">
                                      {extra.label}
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        extra.value === 'Included'
                                          ? 'text-green-600'
                                          : 'text-gray-900'
                                      }`}
                                    >
                                      {extra.value}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              <div
                                className={`mt-5 flex items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-semibold text-primary ${
                                  isSelected ? 'visible' : 'invisible'
                                }`}
                              >
                                <Check size={18} weight="bold" />
                                Selected
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-8 w-full">
                        <SignupFooter
                          selectedRole={selectedRole}
                          currStep={currStep}
                          disableSubmit={!selectedTier}
                          showTooltip={showTooltip}
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          isMobile={screenView === 'mobile'}
                          onBackClick={() =>
                            setStep(ApplicationStep.PersonalInfo)
                          }
                          onSubmitClick={handleContinue}
                        />
                      </div>
                    </div>
                    {screenView === 'desktop' ? (
                      <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                        <TrustSignals variant="sidebar" />
                      </div>
                    ) : (
                      <div className="mb-4 w-full max-w-[600px]">
                        <TrustSignals variant="mobile" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center pt-10"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default ChoosePlan;
